import { c as _c2 } from "react/compiler-runtime";
// Общие детали раздела «Промокоды и партнёры»: подписи, период, плитки цифр,
// редакторы промокода и партнёра. Раздел только на русском (решение
// заказчика 26.09.2026), поэтому строки — прямо в разметке.
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Download, Loader2, Search, X } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { api, type Category, type DownloadedFile, type Partner, type PartnerInput, type PartnerKind, type PromoAudience, type PromoCode, type PromoCodeInput, type PromoOrderStatusGroup, type PromoRewardKind, type PromoState, type SellerShop } from "@/api";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { cn } from "@/lib/utils";

// ── Подписи ─────────────────────────────────────────────────────────────────

export const AUDIENCE_LABEL: Record<PromoAudience, string> = {
  buyer: "Покупательский",
  seller: "Продавцовый"
};
export const AUDIENCE_HINT: Record<PromoAudience, string> = {
  buyer: "Покупатель вводит код при оформлении заказа",
  seller: "Продавец указывает код при регистрации магазина"
};
export const STATE_LABEL: Record<PromoState, string> = {
  active: "Активен",
  inactive: "Выключен",
  scheduled: "Ещё не начался",
  expired: "Истёк",
  exhausted: "Лимит исчерпан",
  archived: "В архиве"
};
export const STATE_CLASS: Record<PromoState, string> = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-muted text-muted-foreground",
  scheduled: "bg-sky-100 text-sky-700",
  expired: "bg-amber-100 text-amber-700",
  exhausted: "bg-amber-100 text-amber-700",
  archived: "bg-muted text-muted-foreground line-through"
};
export const ORDER_GROUP_LABEL: Record<PromoOrderStatusGroup, string> = {
  awaiting: "Ждёт оплаты",
  paid: "Оплачен",
  delivered: "Доставлен",
  returned: "Возврат",
  cancelled: "Отменён"
};
export const ORDER_GROUP_CLASS: Record<PromoOrderStatusGroup, string> = {
  awaiting: "bg-muted text-muted-foreground",
  paid: "bg-sky-100 text-sky-700",
  delivered: "bg-emerald-100 text-emerald-700",
  returned: "bg-amber-100 text-amber-700",
  cancelled: "bg-rose-100 text-rose-700"
};
export const PARTNER_KIND_LABEL: Record<PartnerKind, string> = {
  blogger: "Блогер",
  stylist: "Стилист",
  ambassador: "Амбассадор",
  employee: "Сотрудник",
  agency: "Агентство",
  seller: "Продавец площадки",
  other: "Другое"
};
export const ONBOARDING_LABEL: Record<string, string> = {
  register_pending: "Не завершил регистрацию",
  survey_pending: "Анкета",
  profile_pending: "Заполняет профиль",
  moderation_pending: "На модерации",
  moderation_passed: "Прошёл модерацию",
  active: "Активен"
};
const SOURCE_LABEL: Record<string, string> = {
  registration: "при регистрации",
  link: "по ссылке",
  first_order: "первым заказом"
};
export const sourceLabel = (s: string) => SOURCE_LABEL[s] ?? s;

// ── Форматирование ──────────────────────────────────────────────────────────

export const rub = (n: number): string => `${(Math.round(n * 100) / 100).toLocaleString("ru-RU", {
  maximumFractionDigits: 2
})} ₽`;
export const num = (n: number): string => n.toLocaleString("ru-RU");
export const pct = (share: number | null): string => share == null ? "—" : `${(Math.round(share * 1000) / 10).toLocaleString("ru-RU")} %`;
export const dateRu = (iso: string | null | undefined): string => iso ? new Date(iso).toLocaleDateString("ru-RU", {
  timeZone: "Europe/Moscow"
}) : "—";
export const dateTimeRu = (iso: string | null | undefined): string => iso ? new Date(iso).toLocaleString("ru-RU", {
  timeZone: "Europe/Moscow",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit"
}) : "—";
export const discountLabel = (p: Pick<PromoCode, "kind" | "value" | "audience">): string => {
  if (p.audience === "seller") return "Без скидки";
  if (p.kind === "free_delivery") return "Бесплатная доставка";
  if (p.value <= 0) return "Без скидки (атрибуция)";
  return p.kind === "percent" ? `−${Math.round(p.value * 1000) / 10} %` : `−${num(p.value)} ₽`;
};

/**
 * База вознаграждения — дословно по решению заказчика 26.09.2026: подпись
 * одна и та же в редакторе, в списке и в карточке кода, чтобы никто не
 * гадал, входит ли доставка.
 */
export const REWARD_BASE_BUYER = "от стоимости товаров после скидки, за вычетом возвратов";
export const REWARD_BASE_SELLER = "от комиссии площадки с продаж привлечённого продавца, за вычетом возвратов";
export const rewardBaseLabel = (audience: PromoAudience): string => audience === "seller" ? REWARD_BASE_SELLER : REWARD_BASE_BUYER;
export const rewardLabel = (p: Pick<PromoCode, "rewardKind" | "rewardValue" | "audience">): string => {
  if (p.rewardKind !== "percent" || p.rewardValue <= 0) return "Нет";
  return `${Math.round(p.rewardValue * 1000) / 10} % ${rewardBaseLabel(p.audience)}`;
};
export const saveFile = (file: DownloadedFile) => {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** Сегодня по Москве, ГГГГ-ММ-ДД — так же считает шлюз. */
export const mskToday = (): string => new Date(Date.now() + 3 * 3_600_000).toISOString().slice(0, 10);
const mskDaysAgo = (n: number): string => new Date(Date.now() + 3 * 3_600_000 - n * 86_400_000).toISOString().slice(0, 10);

// ── Мелкие блоки ────────────────────────────────────────────────────────────

export function Pill(t0) {
  const $ = _c2(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function StatTile(t0) {
  const $ = _c2(10);
  const {
    label,
    value,
    hint
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-xs text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="truncate text-lg font-semibold tabular-nums">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== hint) {
    t3 = hint && <span className="text-xs text-muted-foreground">{hint}</span>;
    $[4] = hint;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex min-w-0 flex-col gap-1 rounded-lg border bg-card px-4 py-3">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function CopyButton(t0) {
  const $ = _c2(9);
  const {
    value,
    label,
    testId
  } = t0;
  const [done, setDone] = useState(false);
  let t1;
  if ($[0] !== value) {
    t1 = async () => {
      try {
        await navigator.clipboard.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      } catch {
        window.prompt("\u0421\u043A\u043E\u043F\u0438\u0440\u0443\u0439\u0442\u0435 \u0441\u0441\u044B\u043B\u043A\u0443", value);
      }
    };
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== done) {
    t2 = done ? <Check className="mr-1.5 size-4" /> : <Copy className="mr-1.5 size-4" />;
    $[2] = done;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = done ? "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u043E" : label;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3 || $[7] !== testId) {
    t4 = <Button variant="outline" size="sm" data-testid={testId} onClick={t1}>{t2}{t3}</Button>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = testId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
export type Period = {
  from: string;
  to: string;
};
const PRESETS: {
  key: string;
  label: string;
  days: number | null;
}[] = [{
  key: "7",
  label: "7 дней",
  days: 7
}, {
  key: "30",
  label: "30 дней",
  days: 30
}, {
  key: "90",
  label: "90 дней",
  days: 90
}, {
  key: "all",
  label: "Всё время",
  days: null
}];

/** Период статистики: пресеты и свои даты. Пустой период — «всё время». */
export function PeriodFilter(t0) {
  const $ = _c2(26);
  const {
    value,
    onChange,
    resolved
  } = t0;
  let t1;
  bb0: {
    if (!value.from && !value.to) {
      t1 = "all";
      break bb0;
    }
    if (value.to === mskToday()) {
      for (const p of PRESETS) {
        if (p.days && value.from === mskDaysAgo(p.days - 1)) {
          t1 = p.key;
          break bb0;
        }
      }
    }
    t1 = "custom";
  }
  const active = t1;
  let t2;
  if ($[0] !== active || $[1] !== onChange) {
    t2 = PRESETS.map(p_0 => <button key={p_0.key} type="button" className={cn("rounded px-3 py-1 text-sm", active === p_0.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")} onClick={() => onChange(p_0.days ? {
      from: mskDaysAgo(p_0.days - 1),
      to: mskToday()
    } : {
      from: "",
      to: ""
    })}>{p_0.label}</button>);
    $[0] = active;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="inline-flex rounded-md border p-0.5">{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = value.from || resolved?.from || "";
  const t5 = value.to || undefined;
  let t6;
  if ($[5] !== onChange || $[6] !== value.to) {
    t6 = e => onChange({
      from: e.target.value,
      to: value.to || mskToday()
    });
    $[5] = onChange;
    $[6] = value.to;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = <Input type="date" aria-label={"\u041D\u0430\u0447\u0430\u043B\u043E \u043F\u0435\u0440\u0438\u043E\u0434\u0430"} className="h-8 w-[150px]" value={t4} max={t5} onChange={t6} />;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-muted-foreground">—</span>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = value.to || resolved?.to || "";
  const t10 = value.from || undefined;
  let t11;
  if ($[13] !== onChange || $[14] !== value.from) {
    t11 = e_0 => onChange({
      from: value.from || e_0.target.value,
      to: e_0.target.value
    });
    $[13] = onChange;
    $[14] = value.from;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t9) {
    t12 = <Input type="date" aria-label={"\u041A\u043E\u043D\u0435\u0446 \u043F\u0435\u0440\u0438\u043E\u0434\u0430"} className="h-8 w-[150px]" value={t9} min={t10} onChange={t11} />;
    $[16] = t10;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t7) {
    t13 = <div className="flex items-center gap-1 text-sm">{t7}{t8}{t12}</div>;
    $[20] = t12;
    $[21] = t7;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t3) {
    t14 = <div className="flex flex-wrap items-center gap-2" data-testid="promo-period">{t3}{t13}</div>;
    $[23] = t13;
    $[24] = t3;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
}
export function ExportButtons({
  onExport
}: {
  onExport: (format: "xlsx" | "csv") => Promise<void>;
}) {
  const [busy, setBusy] = useState<null | "xlsx" | "csv">(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (format: "xlsx" | "csv") => {
    setBusy(format);
    setError(null);
    try {
      await onExport(format);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  return <div className="flex items-center gap-2">
			{(["xlsx", "csv"] as const).map(f => <Button key={f} variant="outline" size="sm" disabled={busy !== null} onClick={() => void run(f)} data-testid={`promo-export-${f}`}>
					{busy === f ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Download className="mr-1.5 size-4" />}
					{f === "xlsx" ? "Excel" : "CSV"}
				</Button>)}
			{error && <span className="text-sm text-destructive">{error}</span>}
		</div>;
}

// ── Выбор нескольких значений со строкой поиска ─────────────────────────────

function MultiPick(t0) {
  const $ = _c2(26);
  const {
    label,
    options,
    value,
    onChange,
    placeholder,
    testId
  } = t0;
  const [q, setQ] = useState("");
  let t1;
  if ($[0] !== options) {
    t1 = new Map(options.map(_temp));
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const byId = t1;
  let t2;
  if ($[2] !== byId || $[3] !== label || $[4] !== onChange || $[5] !== options || $[6] !== placeholder || $[7] !== q || $[8] !== testId || $[9] !== value) {
    let t3;
    if ($[11] !== value) {
      t3 = o_0 => !value.includes(o_0.id);
      $[11] = value;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== q) {
      t4 = o_1 => !q.trim() || `${o_1.name} ${o_1.hint ?? ""}`.toLowerCase().includes(q.trim().toLowerCase());
      $[13] = q;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    const filtered = options.filter(t3).filter(t4).slice(0, 30);
    let t5;
    if ($[15] !== label) {
      t5 = <span className="text-muted-foreground">{label}</span>;
      $[15] = label;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== byId || $[18] !== onChange || $[19] !== value) {
      t6 = value.length > 0 && <div className="flex flex-wrap gap-1">{value.map(id => <span key={id} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">{byId.get(id)?.name ?? id}<button type="button" aria-label={"\u0423\u0431\u0440\u0430\u0442\u044C"} onClick={() => onChange(value.filter(v => v !== id))}><X className="size-3" /></button></span>)}</div>;
      $[17] = byId;
      $[18] = onChange;
      $[19] = value;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = e => setQ(e.target.value);
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== placeholder || $[24] !== q) {
      t9 = <div className="relative">{t7}<Input className="pl-8" value={q} onChange={t8} placeholder={placeholder} /></div>;
      $[23] = placeholder;
      $[24] = q;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t2 = <div className="flex flex-col gap-1.5 text-sm" data-testid={testId}>{t5}{t6}{t9}{q.trim() && <div className="max-h-40 overflow-y-auto rounded-md border">{filtered.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Ничего не найдено</div>}{filtered.map(o_2 => <button key={o_2.id} type="button" className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted" onClick={() => {
          onChange([...value, o_2.id]);
          setQ("");
        }}><span className="truncate">{o_2.name}</span>{o_2.hint && <span className="shrink-0 text-xs text-muted-foreground">{o_2.hint}</span>}</button>)}</div>}</div>;
    $[2] = byId;
    $[3] = label;
    $[4] = onChange;
    $[5] = options;
    $[6] = placeholder;
    $[7] = q;
    $[8] = testId;
    $[9] = value;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

// ── Редактор промокода ──────────────────────────────────────────────────────
function _temp(o) {
  return [o.id, o];
}
type DiscountMode = "percent" | "fixed" | "free_delivery" | "none";
type PromoDraft = {
  code: string;
  audience: PromoAudience;
  partnerId: string;
  description: string;
  discountMode: DiscountMode;
  valueInput: string;
  minSubtotal: string;
  maxUses: string;
  perUserLimit: string;
  firstOrderOnly: boolean;
  /** 27.10.2026: только тем, кто не покупал в выбранных магазинах. */
  newToShopOnly: boolean;
  shopIds: string[];
  categoryIds: string[];
  startsAt: string;
  expiresAt: string;
  rewardKind: PromoRewardKind;
  rewardInput: string;
  active: boolean;
  /** fd-promo-34: виден покупателям в «Мои купоны». */
  isPublic: boolean;
  /** fd-promo-19: восстанавливать код при полной отмене заказа. */
  restoreOnCancel: boolean;
};
const toLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const blankPromoDraft = (audience: PromoAudience = "buyer", partnerId = ""): PromoDraft => ({
  code: "",
  audience,
  partnerId,
  description: "",
  discountMode: audience === "seller" ? "none" : "percent",
  valueInput: "10",
  minSubtotal: "0",
  maxUses: "",
  perUserLimit: "1",
  firstOrderOnly: false,
  newToShopOnly: false,
  shopIds: [],
  categoryIds: [],
  startsAt: "",
  expiresAt: "",
  rewardKind: "none",
  rewardInput: "",
  active: true,
  isPublic: false,
  restoreOnCancel: true
});
export const draftFromPromo = (p: PromoCode): PromoDraft => ({
  code: p.code,
  audience: p.audience,
  partnerId: p.partnerId ?? "",
  description: p.description,
  discountMode: p.audience === "seller" ? "none" : p.kind === "free_delivery" ? "free_delivery" : p.value <= 0 ? "none" : p.kind,
  valueInput: p.value <= 0 ? "" : p.kind === "percent" ? String(Math.round(p.value * 1000) / 10) : String(p.value),
  minSubtotal: String(p.minSubtotal),
  maxUses: p.maxUses == null ? "" : String(p.maxUses),
  perUserLimit: p.perUserLimit == null ? "" : String(p.perUserLimit),
  firstOrderOnly: p.firstOrderOnly,
  newToShopOnly: p.newToShopOnly ?? false,
  shopIds: p.shopIds,
  categoryIds: p.categoryIds,
  startsAt: toLocalInput(p.startsAt),
  expiresAt: toLocalInput(p.expiresAt),
  rewardKind: p.rewardKind,
  rewardInput: p.rewardKind === "percent" ? String(Math.round(p.rewardValue * 1000) / 10) : "",
  active: p.active,
  isPublic: p.isPublic,
  restoreOnCancel: p.restoreOnCancel
});
const numberOf = (s: string): number => Number(s.replace(",", ".").replace(/\s/g, ""));
const intOrNull = (s: string): number | null => {
  const v = s.trim();
  if (v === "") return null;
  const n = Math.floor(numberOf(v));
  return Number.isFinite(n) && n >= 0 ? n : null;
};
export const promoInputFrom = (d: PromoDraft): PromoCodeInput => {
  const raw = numberOf(d.valueInput);
  const seller = d.audience === "seller";
  const mode: DiscountMode = seller ? "none" : d.discountMode;
  const value = mode === "none" || mode === "free_delivery" ? 0 : mode === "percent" ? Number.isFinite(raw) ? raw / 100 : 0 : raw;
  const reward = numberOf(d.rewardInput);
  const localToIso = (s: string): string | null => s ? new Date(s).toISOString() : null;
  return {
    code: d.code.trim().toUpperCase(),
    audience: d.audience,
    kind: mode === "fixed" ? "fixed" : mode === "free_delivery" ? "free_delivery" : "percent",
    value: Number.isFinite(value) && value >= 0 ? value : 0,
    minSubtotal: seller ? 0 : Math.max(0, numberOf(d.minSubtotal) || 0),
    maxUses: seller ? null : intOrNull(d.maxUses),
    perUserLimit: seller ? null : d.perUserLimit.trim() === "" ? null : Math.max(1, Math.floor(numberOf(d.perUserLimit)) || 1),
    active: d.active,
    description: d.description.trim(),
    startsAt: localToIso(d.startsAt),
    expiresAt: localToIso(d.expiresAt),
    partnerId: d.partnerId || null,
    rewardKind: d.rewardKind,
    rewardValue: d.rewardKind !== "percent" || !Number.isFinite(reward) || reward < 0 ? 0 : reward / 100,
    firstOrderOnly: seller ? false : d.firstOrderOnly,
    newToShopOnly: seller ? false : d.newToShopOnly,
    shopIds: seller ? [] : d.shopIds,
    categoryIds: seller ? [] : d.categoryIds,
    isPublic: seller ? false : d.isPublic,
    restoreOnCancel: d.restoreOnCancel
  };
};
const suggestCode = (partner?: Partner): string => {
  const base = (partner?.name ?? "OSMIX").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
  return `${base || "PROMO"}${Math.floor(10 + Math.random() * 89)}`;
};
function Field(t0) {
  const $ = _c2(8);
  const {
    label,
    hint,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-muted-foreground">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = hint && <span className="text-xs text-muted-foreground">{hint}</span>;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t2) {
    t3 = <label className="flex flex-col gap-1 text-sm">{t1}{children}{t2}</label>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
const selectClass = "h-9 rounded-md border border-input bg-background px-3 text-sm";
export function PromoEditorDialog({
  open,
  initial,
  editingCode,
  onClose,
  onSaved
}: {
  open: boolean;
  initial: PromoDraft;
  /** Код при правке; при создании — undefined. */
  editingCode?: string;
  onClose: () => void;
  onSaved: (promo: PromoCode) => void;
}) {
  const [draft, setDraft] = useState<PromoDraft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const partnersQ = useQuery({
    queryKey: ["partners", "list", ""],
    queryFn: () => api.partners.list(),
    enabled: open
  });
  const shopsQ = useQuery({
    queryKey: ["sellers", "list"],
    queryFn: () => api.sellers.list(),
    enabled: open
  });
  const categoriesQ = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.categories.list(),
    enabled: open
  });
  const partners = partnersQ.data ?? [];
  const set = <K extends keyof PromoDraft,>(key: K, value: PromoDraft[K]) => setDraft(d => ({
    ...d,
    [key]: value
  }));
  const seller = draft.audience === "seller";
  const shopOptions = (shopsQ.data ?? []).map((s: SellerShop) => ({
    id: s.shopId ?? s.id,
    name: s.shopName || s.sellerName,
    hint: s.email
  }));
  const categoryOptions = (categoriesQ.data ?? []).map((c: Category) => ({
    id: c.id,
    name: c.name
  }));
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const input = promoInputFrom(draft);
      const saved = editingCode ? await api.promos.update(editingCode, (({
        code: _c,
        ...patch
      }) => patch)(input)) : await api.promos.create(input);
      onSaved(saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const rewardHint = rewardBaseLabel(draft.audience);
  return <Dialog open={open} onOpenChange={next => !next && onClose()}>
			<DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="promo-editor">
				<DialogHeader>
					<DialogTitle>{editingCode ? `Промокод ${editingCode}` : "Новый промокод"}</DialogTitle>
					<DialogDescription>{AUDIENCE_HINT[draft.audience]}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-5">
					<div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Тип промокода">
						{(["buyer", "seller"] as const).map(a => <button key={a} type="button" role="radio" aria-checked={draft.audience === a} data-testid={`promo-audience-${a}`} onClick={() => setDraft(d_0 => ({
            ...d_0,
            audience: a,
            discountMode: a === "seller" ? "none" : d_0.discountMode === "none" && !editingCode ? "percent" : d_0.discountMode
          }))} className={cn("rounded-lg border px-3 py-2 text-left text-sm", draft.audience === a ? "border-primary bg-primary/5" : "hover:bg-muted")}>
								<div className="font-medium">{AUDIENCE_LABEL[a]}</div>
								<div className="text-xs text-muted-foreground">{AUDIENCE_HINT[a]}</div>
							</button>)}
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<Field label="Код" hint="Латиница, цифры, дефис; попадёт в ссылку ?promo=">
							<div className="flex gap-2">
								<Input data-testid="promo-code-input" value={draft.code} disabled={Boolean(editingCode) || busy} onChange={e_0 => set("code", e_0.target.value.toUpperCase().replace(/\s/g, ""))} placeholder="BLOGGER10" />
								{!editingCode && <Button type="button" variant="outline" onClick={() => set("code", suggestCode(partners.find(p => p.id === draft.partnerId)))}>
										Придумать
									</Button>}
							</div>
						</Field>
						<Field label="Владелец (партнёр)" hint="Партнёров заводят на вкладке «Партнёры»">
							<select className={selectClass} data-testid="promo-partner-select" value={draft.partnerId} onChange={e_1 => set("partnerId", e_1.target.value)}>
								<option value="">Без партнёра — код площадки</option>
								{partners.map(p_0 => <option key={p_0.id} value={p_0.id}>
										{p_0.name} · {PARTNER_KIND_LABEL[p_0.kind]}
									</option>)}
							</select>
						</Field>
						<Field label="Описание (видно только в консоли)">
							<Input value={draft.description} onChange={e_2 => set("description", e_2.target.value)} placeholder="Осенняя интеграция в Telegram" />
						</Field>
						<label className="flex items-center gap-2 self-end pb-2 text-sm">
							<input type="checkbox" checked={draft.active} onChange={e_3 => set("active", e_3.target.checked)} />
							Активен
						</label>
					</div>

					{!seller && <section className="flex flex-col gap-3 rounded-lg border p-4">
							<h3 className="text-sm font-medium">Скидка и условия</h3>
							<div className="grid gap-4 sm:grid-cols-3">
								<Field label="Скидка">
									<select className={selectClass} data-testid="promo-discount-mode" value={draft.discountMode} onChange={e_4 => set("discountMode", e_4.target.value as DiscountMode)}>
										<option value="percent">Процент</option>
										<option value="fixed">Фиксированная сумма</option>
										<option value="free_delivery">Бесплатная доставка</option>
										<option value="none">Без скидки — только атрибуция</option>
									</select>
								</Field>
								{(draft.discountMode === "percent" || draft.discountMode === "fixed") && <Field label={draft.discountMode === "percent" ? "Размер, %" : "Размер, ₽"}>
										<Input type="number" min={0} value={draft.valueInput} onChange={e_5 => set("valueInput", e_5.target.value)} />
									</Field>}
								<Field label="Мин. сумма заказа, ₽">
									<Input type="number" min={0} value={draft.minSubtotal} onChange={e_6 => set("minSubtotal", e_6.target.value)} />
								</Field>
								<Field label="Всего использований" hint="Пусто — без лимита">
									<Input type="number" min={0} value={draft.maxUses} onChange={e_7 => set("maxUses", e_7.target.value)} placeholder="∞" />
								</Field>
								<Field label="На одного покупателя" hint="Пусто — без лимита">
									<Input type="number" min={1} value={draft.perUserLimit} onChange={e_8 => set("perUserLimit", e_8.target.value)} placeholder="∞" />
								</Field>
								<label className="flex items-center gap-2 self-center text-sm">
									<input type="checkbox" data-testid="promo-is-public" checked={draft.isPublic} onChange={e_9 => set("isPublic", e_9.target.checked)} />
									Показывать покупателям в «Мои купоны»
								</label>
								<label className="flex items-center gap-2 self-center text-sm">
									<input type="checkbox" data-testid="promo-restore-on-cancel" checked={draft.restoreOnCancel} onChange={e_10 => set("restoreOnCancel", e_10.target.checked)} />
									Возвращать код при полной отмене заказа
								</label>
							</div>
							{draft.discountMode === "free_delivery" && <p className="text-xs text-muted-foreground">
									Доставка групп с подходящими товарами для покупателя бесплатна, перевозчику площадка платит полный тариф.
								</p>}
							{/* PDF «Доработки платформы» п.1 (27.10.2026): кому действует код. */}
							<div className="flex flex-col gap-2 rounded-md bg-muted/40 p-3" data-testid="promo-audience-flags">
								<span className="text-xs font-medium text-muted-foreground">Кому действует</span>
								<label className="flex items-center gap-2 text-sm">
									<input type="checkbox" data-testid="promo-first-order-only" checked={draft.firstOrderOnly} onChange={e_11 => set("firstOrderOnly", e_11.target.checked)} />
									Только для новых пользователей платформы (ни одного оплаченного заказа на OSMIX)
								</label>
								<label className="flex items-center gap-2 text-sm">
									<input type="checkbox" data-testid="promo-new-to-shop-only" checked={draft.newToShopOnly} onChange={e_12 => set("newToShopOnly", e_12.target.checked)} />
									Только для пользователей, которые не покупали в выбранных магазинах
								</label>
								{draft.newToShopOnly && draft.shopIds.length === 0 && <p className="text-xs text-destructive">Выберите магазины ниже - без них непонятно, в каком магазине покупатель должен быть новым.</p>}
							</div>
							<div className="grid gap-4 sm:grid-cols-2">
								<MultiPick label="Только товары магазинов (пусто — все)" placeholder="Найти магазин" options={shopOptions} value={draft.shopIds} onChange={v => set("shopIds", v)} testId="promo-shops" />
								<MultiPick label="Только категории, с подкатегориями (пусто — все)" placeholder="Найти категорию" options={categoryOptions} value={draft.categoryIds} onChange={v_0 => set("categoryIds", v_0)} testId="promo-categories" />
							</div>
						</section>}

					<section className="grid gap-4 rounded-lg border p-4 sm:grid-cols-2">
						<h3 className="text-sm font-medium sm:col-span-2">Срок действия</h3>
						<Field label="Начало (необязательно)">
							<Input type="datetime-local" value={draft.startsAt} onChange={e_13 => set("startsAt", e_13.target.value)} />
						</Field>
						<Field label="Окончание - код выключится сам (необязательно)">
							<Input type="datetime-local" value={draft.expiresAt} onChange={e_14 => set("expiresAt", e_14.target.value)} />
						</Field>
					</section>

					<section className="flex flex-col gap-3 rounded-lg border p-4">
						<h3 className="text-sm font-medium">Вознаграждение партнёра</h3>
						<div className="grid gap-4 sm:grid-cols-2">
							<Field label="Вид">
								<select className={selectClass} data-testid="promo-reward-kind" value={draft.rewardKind} onChange={e_15 => set("rewardKind", e_15.target.value as PromoRewardKind)}>
									<option value="none">Без вознаграждения</option>
									<option value="percent">Процент</option>
								</select>
							</Field>
							{draft.rewardKind !== "none" && <Field label="Размер, %" hint={rewardHint}>
									<Input type="number" min={0} max={100} step="0.1" data-testid="promo-reward-value" value={draft.rewardInput} onChange={e_16 => set("rewardInput", e_16.target.value)} />
								</Field>}
						</div>
						<p className="text-xs text-muted-foreground">
							В статистике считается «начислено партнёру». Выплаты партнёрам пока не проводятся — это следующий этап партнёрской программы.
						</p>
					</section>

					{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={busy}>
						Отмена
					</Button>
					<Button onClick={() => void submit()} disabled={busy || !draft.code.trim()} data-testid="promo-save">
						{busy && <Loader2 className="mr-2 size-4 animate-spin" />}
						Сохранить
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}

// ── Редактор партнёра ───────────────────────────────────────────────────────

export const blankPartner = (): PartnerInput => ({
  name: "",
  kind: "blogger",
  contactName: "",
  phone: "",
  email: "",
  telegram: "",
  note: "",
  userId: null,
  active: true
});
export const partnerInputFrom = (p: Partner): PartnerInput => ({
  name: p.name,
  kind: p.kind,
  contactName: p.contactName,
  phone: p.phone,
  email: p.email,
  telegram: p.telegram,
  note: p.note,
  userId: p.userId,
  active: p.active
});
export function PartnerEditorDialog({
  open,
  initial,
  editingId,
  onClose,
  onSaved
}: {
  open: boolean;
  initial: PartnerInput;
  editingId?: string;
  onClose: () => void;
  onSaved: (p: Partner) => void;
}) {
  const [draft, setDraft] = useState<PartnerInput>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const usersQ = useQuery({
    queryKey: ["users", "list"],
    queryFn: () => api.users.list(),
    enabled: open
  });
  const set = <K extends keyof PartnerInput,>(key: K, value: PartnerInput[K]) => setDraft(d => ({
    ...d,
    [key]: value
  }));
  const users = usersQ.data ?? [];
  const linked = users.find(u => u.id === draft.userId);
  const matches = userQuery.trim() ? users.filter(u_0 => `${u_0.name} ${u_0.email} ${u_0.phone} ${u_0.shopName ?? ""}`.toLowerCase().includes(userQuery.trim().toLowerCase())).slice(0, 8) : [];
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = editingId ? await api.partners.update(editingId, draft) : await api.partners.create(draft);
      onSaved(saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={open} onOpenChange={next => !next && onClose()}>
			<DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="partner-editor">
				<DialogHeader>
					<DialogTitle>{editingId ? "Партнёр" : "Новый партнёр"}</DialogTitle>
					<DialogDescription>Владелец промокодов: блогер, стилист, амбассадор, сотрудник или продавец площадки.</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 sm:grid-cols-2">
					<Field label="Имя или название">
						<Input data-testid="partner-name" value={draft.name} onChange={e_0 => set("name", e_0.target.value)} placeholder="Мария Иванова / @maria_style" />
					</Field>
					<Field label="Кто это">
						<select className={selectClass} value={draft.kind} onChange={e_1 => set("kind", e_1.target.value as PartnerKind)}>
							{(Object.keys(PARTNER_KIND_LABEL) as PartnerKind[]).map(k => <option key={k} value={k}>
									{PARTNER_KIND_LABEL[k]}
								</option>)}
						</select>
					</Field>
					<Field label="Контактное лицо">
						<Input value={draft.contactName} onChange={e_2 => set("contactName", e_2.target.value)} />
					</Field>
					<Field label="Телефон">
						<Input value={draft.phone} onChange={e_3 => set("phone", e_3.target.value)} placeholder="+7 900 000-00-00" />
					</Field>
					<Field label="Почта">
						<Input type="email" value={draft.email} onChange={e_4 => set("email", e_4.target.value)} />
					</Field>
					<Field label="Telegram">
						<Input value={draft.telegram} onChange={e_5 => set("telegram", e_5.target.value)} placeholder="@username" />
					</Field>
					<div className="flex flex-col gap-1 text-sm sm:col-span-2">
						<span className="text-muted-foreground">Пользователь площадки (если партнёр уже зарегистрирован)</span>
						{linked || draft.userId ? <div className="flex items-center justify-between rounded-md border px-3 py-2">
								<span>
									{linked ? `${linked.name || linked.email} · ${linked.email}` : draft.userId}
									{linked?.shopName && <span className="text-muted-foreground"> · {linked.shopName}</span>}
								</span>
								<Button variant="ghost" size="sm" onClick={() => set("userId", null)}>
									Отвязать
								</Button>
							</div> : <>
								<Input value={userQuery} onChange={e_6 => setUserQuery(e_6.target.value)} placeholder="Найти по имени, почте, телефону, магазину" />
								{matches.length > 0 && <div className="max-h-44 overflow-y-auto rounded-md border">
										{matches.map(u_1 => <button key={u_1.id} type="button" className="flex w-full justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted" onClick={() => {
                set("userId", u_1.id);
                setUserQuery("");
              }}>
												<span className="truncate">{u_1.name || u_1.email}</span>
												<span className="shrink-0 text-xs text-muted-foreground">{u_1.shopName || u_1.email}</span>
											</button>)}
									</div>}
							</>}
					</div>
					<Field label="Заметка">
						<Input value={draft.note} onChange={e_7 => set("note", e_7.target.value)} placeholder="Условия договорённости, ссылки на соцсети" />
					</Field>
					<label className="flex items-center gap-2 self-end pb-2 text-sm">
						<input type="checkbox" checked={draft.active} onChange={e_8 => set("active", e_8.target.checked)} />
						Активен
					</label>
				</div>
				{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={busy}>
						Отмена
					</Button>
					<Button onClick={() => void submit()} disabled={busy || !draft.name.trim()} data-testid="partner-save">
						{busy && <Loader2 className="mr-2 size-4 animate-spin" />}
						Сохранить
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}
export function StateBadge(t0) {
  const $ = _c2(3);
  const {
    state
  } = t0;
  const t1 = STATE_CLASS[state];
  const t2 = STATE_LABEL[state];
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <Pill className={t1}>{t2}</Pill>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function AudienceBadge(t0) {
  const $ = _c2(2);
  const {
    audience
  } = t0;
  const t1 = AUDIENCE_LABEL[audience];
  let t2;
  if ($[0] !== t1) {
    t2 = <Badge variant="outline" className="whitespace-nowrap font-normal">{t1}</Badge>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}